import { useState, useCallback } from 'react'
import { Routes, Route } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Header } from './components/Header'
import { FileUpload } from './components/FileUpload'
import { JobConfig } from './components/JobConfig'
import { JobList } from './components/JobList'
import { JobDetail } from './components/JobDetail'
import { Settings } from './components/Settings'
import { type UploadResponse } from './lib/api'

function Dashboard() {
  const [uploadedFiles, setUploadedFiles] = useState<UploadResponse[]>([])
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>()
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [settingsExpanded, setSettingsExpanded] = useState(false)

  const handleUploadComplete = useCallback((files: UploadResponse[]) => {
    setUploadedFiles(files)
  }, [])

  const handleJobCreated = useCallback((jobId: string) => {
    setSelectedJobId(jobId)
    setUploadedFiles([])
    setRefreshTrigger(prev => prev + 1)
  }, [])

  const handleSelectJob = useCallback((jobId: string) => {
    setSelectedJobId(jobId)
  }, [])

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-full">
      <div className="lg:w-80 xl:w-96 flex-shrink-0 space-y-4">
        <div className="card animate-in">
          <FileUpload onUploadComplete={handleUploadComplete} />
        </div>

        <div className="card animate-in" style={{ animationDelay: '50ms' }}>
          <JobConfig
            uploadedFiles={uploadedFiles}
            onJobCreated={handleJobCreated}
          />
        </div>

        <div className="card animate-in" style={{ animationDelay: '100ms' }}>
          <button
            onClick={() => setSettingsExpanded(!settingsExpanded)}
            className="w-full flex items-center justify-between text-left"
          >
            <span className="section-title text-base">
              GPU Configuration
            </span>
            {settingsExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {settingsExpanded && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <Settings compact />
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-w-0 space-y-4">
        <div className="card animate-in" style={{ animationDelay: '75ms' }}>
          <JobList
            onSelectJob={handleSelectJob}
            selectedJobId={selectedJobId}
            refreshTrigger={refreshTrigger}
          />
        </div>

        {selectedJobId ? (
          <div className="animate-in" style={{ animationDelay: '100ms' }}>
            <JobDetail jobId={selectedJobId} />
          </div>
        ) : (
          <div className="card text-center py-16 animate-in" style={{ animationDelay: '100ms' }}>
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-800/50 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <p className="text-gray-400 text-lg font-medium">No job selected</p>
            <p className="text-gray-500 text-sm mt-1">
              Select a job from the list above or create a new one
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function App() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/job/:jobId" element={<JobDetailPage />} />
        </Routes>
      </main>
      
      <footer className="border-t border-white/5 py-4 mt-auto">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between text-sm text-gray-500">
          <span>GPU Stem Separation</span>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
              Powered by Demucs
            </span>
            <span className="hidden sm:inline">Multi-GPU Orchestration with Ray</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

function JobDetailPage() {
  const jobId = window.location.pathname.split('/').pop() || ''
  return (
    <div className="animate-in">
      <JobDetail jobId={jobId} />
    </div>
  )
}

export default App
